import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    Shield, Copy, Eye,
    ChevronRight, Check, ChevronDown, ChevronUp, Download, Globe, AlertCircle, Sparkles
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase/config";
import { collection, query, where, onSnapshot, limit } from "firebase/firestore";
import jsPDF from "jspdf";
import ForensicBot from "../pages/ForensicBot"; // Make sure the path is correct
import Footer from "../components/Footer";

const CAPTURE_SECTIONS = [
    {
        title: "🌐 NETWORK & IP",
        id: "network",
        fields: [
            ["IP Address", c => c.ip],
            ["ISP", c => c.isp],
            ["Organisation", c => c.org],
            ["ASN", c => c.asn],
            ["Hostname", c => c.hostname],
            ["Connection Type", c => c.connectionType],
            ["Connection Type 2", c => c.connectionType2],
            ["Downlink", c => c.connectionDownlink ? c.connectionDownlink + " Mbps" : null],
            ["Downlink Max", c => c.connectionDownlinkMax ? c.connectionDownlinkMax + " Mbps" : null],
            ["RTT", c => c.connectionRtt ? c.connectionRtt + " ms" : null],
            ["Save Data", c => c.connectionSaveData != null ? String(c.connectionSaveData) : null],
            ["Local IP (WebRTC)", c => c.webRTCIP],
            ["HTTP Version", c => c.httpVersion],
            ["TLS Protocol", c => c.tlsProtocol],
            ["TLS Cipher", c => c.tlsCipher],
            ["VPN/Datacenter", c => c.isHosting ? "Likely VPN" : "No"],
            ["Proxy Detected", c => c.isProxy ? "Yes" : "No"],
            ["Mail Proxy", c => c.proxyLabel],
            ["Email Client", c => c.emailClient],
            ["Is Bot", c => c.isBot ? "Yes" : "No"],
            ["Mobile Network", c => c.isMobileNetwork != null ? String(c.isMobileNetwork) : null],
        ]
    },
    {
        title: "📋 HTTP REQUEST HEADERS",
        id: "headers",
        fields: [
            ["Accept-Language", c => c.headerAcceptLanguage],
            ["DPR", c => c.headerDPR],
            ["Viewport-Width", c => c.headerViewportWidth],
            ["Device-Memory", c => c.headerDeviceMemory],
            ["Downlink", c => c.headerDownlink],
            ["ECT", c => c.headerECT],
            ["RTT", c => c.headerRTT],
            ["Sec-CH-UA-Platform", c => c.headerSecCHUAPlatform],
            ["Sec-CH-UA-Mobile", c => c.headerSecCHUAMobile],
            ["Sec-CH-UA", c => c.headerSecCHUA],
            ["Upgrade-Insecure-Req", c => c.headerUpgradeInsecureReq],
            ["Cache-Control", c => c.headerCacheControl],
            ["Accept-Encoding", c => c.headerAcceptEncoding],
            ["Connection", c => c.headerConnection],
            ["Priority", c => c.headerPriority],
        ]
    },
    {
        title: "📡 IP LOCATION (APPROXIMATE)",
        id: "ip_location",
        fields: [
            ["City", c => c.city],
            ["Region", c => c.region],
            ["Country", c => c.country],
            ["Country Code", c => c.countryCode],
            ["ZIP", c => c.zip],
            ["Coordinates", c => c.lat ? `${c.lat}, ${c.lon}` : null],
        ]
    },
    {
        title: "🛰️ GPS LOCATION (EXACT)",
        id: "gps_location",
        fields: [
            ["GPS Coords", c => c.gpsLat ? `${c.gpsLat}, ${c.gpsLon}` : null],
            ["Accuracy", c => c.gpsAccuracy ? `${c.gpsAccuracy} metres` : null],
            ["Address", c => c.gpsAddress],
            ["City", c => c.gpsCity],
            ["State", c => c.gpsState],
            ["District", c => c.gpsDistrict],
            ["Locality", c => c.gpsLocality],
            ["Road", c => c.gpsRoad],
            ["Pincode", c => c.gpsPincode],
            ["Country", c => c.gpsCountry],
        ]
    },
    {
        title: "📱 DEVICE & HARDWARE",
        id: "hardware",
        fields: [
            ["Device Type", c => c.device],
            ["OS", c => c.os],
            ["Browser", c => c.browser],
            ["Browser Version", c => c.browserVersion],
            ["Platform", c => c.platform],
            ["CPU Cores", c => c.cpuCores],
            ["CPU", c => c.cpu],
            ["RAM", c => c.ram ? `${c.ram} GB` : null],
            ["GPU", c => c.gpu],
            ["GPU Vendor", c => c.gpuVendor],
            ["Touch Points", c => c.maxTouchPoints],
            ["Has Touch", c => c.hasTouch != null ? String(c.hasTouch) : null],
        ]
    },
    {
        title: "🎮 WEBGL & GPU",
        id: "webgl",
        fields: [
            ["WebGL Renderer", c => c.webglRenderer],
            ["WebGL Vendor", c => c.webglVendor],
            ["WebGL Version", c => c.webglVersion],
            ["Shading Language", c => c.webglShadingLanguageVersion],
            ["Extensions Count", c => c.webglExtensions],
            ["Max Texture Size", c => c.webglMaxTextureSize],
            ["Max Viewport W", c => c.webglMaxViewportWidth],
            ["Max Viewport H", c => c.webglMaxViewportHeight],
            ["Antialiasing", c => c.webglAntialiasing != null ? String(c.webglAntialiasing) : null],
        ]
    },
    {
        title: "🔋 BATTERY",
        id: "battery",
        fields: [
            ["Battery Level", c => c.batteryLevel != null ? `${c.batteryLevel}%` : null],
            ["Charging", c => c.batteryCharging != null ? (c.batteryCharging ? "Yes ⚡" : "No") : null],
            ["Charging Time", c => c.batteryChargingTime != null ? `${c.batteryChargingTime}s` : null],
            ["Discharging Time", c => c.batteryDischargingTime != null ? `${c.batteryDischargingTime}s` : null],
        ]
    },
    {
        title: "🖥️ SCREEN & DISPLAY",
        id: "screen",
        fields: [
            ["Resolution", c => c.screenWidth ? `${c.screenWidth}x${c.screenHeight}` : null],
            ["Available", c => c.screenAvailWidth ? `${c.screenAvailWidth}x${c.screenAvailHeight}` : null],
            ["Window", c => c.windowWidth ? `${c.windowWidth}x${c.windowHeight}` : null],
            ["Outer", c => c.outerWidth ? `${c.outerWidth}x${c.outerHeight}` : null],
            ["Screen X", c => c.screenX],
            ["Screen Y", c => c.screenY],
            ["Scroll X", c => c.scrollX],
            ["Scroll Y", c => c.scrollY],
            ["Color Depth", c => c.colorDepth ? `${c.colorDepth} bit` : null],
            ["Pixel Depth", c => c.pixelDepth ? `${c.pixelDepth} bit` : null],
            ["Pixel Ratio", c => c.pixelRatio],
            ["Device Scale", c => c.deviceScaleFactor],
            ["Color Gamut", c => c.colorGamut],
            ["Orientation", c => c.screenOrientation],
            ["Orientation Angle", c => c.screenOrientationAngle != null ? `${c.screenOrientationAngle}°` : null],
            ["Orientation Type", c => c.screenOrientationType],
            ["Extended Display", c => c.screenIsExtended != null ? String(c.screenIsExtended) : null],
            ["Fullscreen", c => c.isFullscreen != null ? String(c.isFullscreen) : null],
            ["Visibility", c => c.documentVisibility],
            ["HDR Support", c => c.hdrSupport != null ? String(c.hdrSupport) : null],
            ["Wide Color Gamut", c => c.wideColorGamut != null ? String(c.wideColorGamut) : null],
            ["Screen Color Depth", c => c.screenColorDepth],
            ["Screen Left", c => c.windowScreenLeft],
            ["Screen Top", c => c.windowScreenTop],
            ["Visual VP Width", c => c.visualViewportWidth],
            ["Visual VP Height", c => c.visualViewportHeight],
            ["Visual VP Scale", c => c.visualViewportScale],
        ]
    },
    {
        title: "🔍 BROWSER DETAILS",
        id: "browser_details",
        fields: [
            ["Language", c => c.language],
            ["Languages", c => c.languages],
            ["Platform", c => c.platform],
            ["App Name", c => c.appName],
            ["App Version", c => c.appVersion],
            ["App Code Name", c => c.appCodeName],
            ["Product", c => c.product],
            ["Product Sub", c => c.productSub],
            ["Vendor", c => c.vendor],
            ["Vendor Sub", c => c.vendorSub],
            ["Cookies", c => c.cookiesEnabled != null ? (c.cookiesEnabled ? "Enabled" : "Disabled") : null],
            ["Do Not Track", c => c.doNotTrack],
            ["History Length", c => c.historyLength],
            ["Referrer", c => c.referrer],
            ["Online", c => c.onLine != null ? String(c.onLine) : null],
            ["PDF Viewer", c => c.pdfViewerEnabled != null ? String(c.pdfViewerEnabled) : null],
            ["Java Enabled", c => c.javaEnabled != null ? String(c.javaEnabled) : null],
            ["Doc Title", c => c.documentTitle],
            ["Doc Charset", c => c.documentCharset],
            ["Compat Mode", c => c.documentCompatMode],
            ["Ready State", c => c.documentReadyState],
            ["Plugins Count", c => c.pluginsCount],
            ["MIME Types", c => c.mimeTypesCount],
            ["Plugins", c => c.plugins],
            ["User Agent", c => c.userAgent],
            ["Build ID", c => c.navigatorBuildID],
            ["Navigation Type", c => c.navigationType],
            ["Resource Count", c => c.resourceTimingCount],
            ["Secure Context", c => c.isSecureContext != null ? String(c.isSecureContext) : null],
            ["Cross-Origin Isolated", c => c.crossOriginIsolated != null ? String(c.crossOriginIsolated) : null],
        ]
    },
    {
        title: "🌍 TIMEZONE & LOCALE",
        id: "timezone",
        fields: [
            ["Timezone", c => c.timezone],
            ["Timezone Offset", c => c.timezoneOffset != null ? `${c.timezoneOffset} min` : null],
            ["TZ Offset Min", c => c.timezoneOffsetMinutes],
            ["Locale", c => c.locale],
            ["Hour Cycle", c => c.hourCycle],
            ["Calendar", c => c.calendar],
            ["Numbering System", c => c.numberingSystem],
        ]
    },
    {
        title: "💾 STORAGE",
        id: "storage",
        fields: [
            ["Storage Quota", c => c.storageQuota != null ? `${c.storageQuota} MB` : null],
            ["Storage Usage", c => c.storageUsage != null ? `${c.storageUsage} MB` : null],
        ]
    },
    {
        title: "🎤 MEDIA DEVICES",
        id: "media",
        fields: [
            ["Audio Inputs", c => c.audioInputCount],
            ["Audio Outputs", c => c.audioOutputCount],
            ["Video Inputs", c => c.videoInputCount],
        ]
    },
    {
        title: "🔒 PERMISSIONS",
        id: "permissions",
        fields: [
            ["Camera", c => c.permCamera],
            ["Microphone", c => c.permMicrophone],
            ["Notifications", c => c.permNotifications],
            ["Geolocation", c => c.permGeolocation],
            ["Clipboard", c => c.permClipboard],
        ]
    },
    {
        title: "⚡ PERFORMANCE",
        id: "performance",
        fields: [
            ["Page Load Time", c => c.pageLoadTime != null ? `${c.pageLoadTime} ms` : null],
            ["DOM Content Loaded", c => c.domContentLoaded != null ? `${c.domContentLoaded} ms` : null],
            ["Time to First Byte", c => c.timeToFirstByte != null ? `${c.timeToFirstByte} ms` : null],
            ["JS Heap Limit", c => c.deviceMemoryHeap != null ? `${c.deviceMemoryHeap} MB` : null],
            ["Used JS Heap", c => c.usedJSHeap != null ? `${c.usedJSHeap} MB` : null],
        ]
    },
    {
        title: "🕵️ PRIVACY",
        id: "privacy",
        fields: [
            ["Incognito", c => c.incognito != null ? (c.incognito ? "Yes 🕵️" : "No") : null],
            ["Ad Blocker", c => c.adBlockEnabled != null ? (c.adBlockEnabled ? "Yes" : "No") : null],
            ["Canvas Spoofed", c => c.canvasSpoofed != null ? (c.canvasSpoofed ? "Yes (spoofed)" : "No") : null],
            ["Fingerprint Resist.", c => c.fingerprintingResistance != null ? (c.fingerprintingResistance ? "Yes" : "No") : null],
            ["Bluetooth Adapter", c => c.bluetoothAdapter != null ? String(c.bluetoothAdapter) : null],
        ]
    },
    {
        title: "🖱️ INPUT & DISPLAY",
        id: "input",
        fields: [
            ["Hover", c => c.hoverCapability],
            ["Pointer Type", c => c.pointerType],
            ["Pointer Coarse", c => c.pointerCoarse != null ? String(c.pointerCoarse) : null],
            ["Pointer Fine", c => c.pointerFine != null ? String(c.pointerFine) : null],
            ["Any Hover", c => c.anyHover != null ? String(c.anyHover) : null],
            ["Any Pointer", c => c.anyPointer],
            ["Display Mode", c => c.displayMode],
            ["Prefers Dark", c => c.prefersColorScheme],
            ["Reduced Motion", c => c.prefersReducedMotion != null ? String(c.prefersReducedMotion) : null],
            ["High Contrast", c => c.prefersContrast],
            ["Forced Colors", c => c.forcedColors != null ? String(c.forcedColors) : null],
        ]
    },
    {
        title: "🔬 FINGERPRINTS",
        id: "fingerprints",
        fields: [
            ["Canvas Hash", c => c.canvasHash],
            ["Audio Fingerprint", c => c.audioFingerprint],
            ["Font Count", c => c.fontCount],
            ["Math Sin", c => c.mathSin],
            ["Math Cos", c => c.mathCos],
            ["Math Tan", c => c.mathTan],
            ["Math Log", c => c.mathLog],
            ["Math Sqrt", c => c.mathSqrt],
            ["Math Asin", c => c.mathAsin],
            ["Math Sinh", c => c.mathSinh],
            ["Math Cosh", c => c.mathCosh],
            ["Math Expm1", c => c.mathExpm1],
        ]
    },
    {
        title: "🌐 BROWSER FEATURES",
        id: "browser_features",
        fields: [
            ["Service Worker", c => c.hasServiceWorker != null ? String(c.hasServiceWorker) : null],
            ["Web Worker", c => c.hasWebWorker != null ? String(c.hasWebWorker) : null],
            ["WebSocket", c => c.hasWebSocket != null ? String(c.hasWebSocket) : null],
            ["IndexedDB", c => c.hasIndexedDB != null ? String(c.hasIndexedDB) : null],
            ["WebRTC", c => c.hasWebRTC != null ? String(c.hasWebRTC) : null],
            ["Canvas", c => c.hasCanvas != null ? String(c.hasCanvas) : null],
            ["WebGL", c => c.hasWebGL != null ? String(c.hasWebGL) : null],
            ["WebGL2", c => c.hasWebGL2 != null ? String(c.hasWebGL2) : null],
            ["Bluetooth", c => c.hasBluetooth != null ? String(c.hasBluetooth) : null],
            ["USB", c => c.hasUSB != null ? String(c.hasUSB) : null],
            ["NFC", c => c.hasNFC != null ? String(c.hasNFC) : null],
            ["Gamepad", c => c.hasGamepad != null ? String(c.hasGamepad) : null],
            ["Vibration", c => c.hasVibration != null ? String(c.hasVibration) : null],
            ["Geolocation", c => c.hasGeolocation != null ? String(c.hasGeolocation) : null],
            ["Battery API", c => c.hasBattery != null ? String(c.hasBattery) : null],
            ["Device Memory", c => c.hasDeviceMemory != null ? String(c.hasDeviceMemory) : null],
            ["Hardware Concurrency", c => c.hasHardwareConcurrency != null ? String(c.hasHardwareConcurrency) : null],
            ["Credentials", c => c.hasCredentials != null ? String(c.hasCredentials) : null],
            ["Share", c => c.hasShare != null ? String(c.hasShare) : null],
            ["Clipboard", c => c.hasClipboard != null ? String(c.hasClipboard) : null],
            ["Wake Lock", c => c.hasWakeLock != null ? String(c.hasWakeLock) : null],
            ["Speech Recog.", c => c.hasSpeechRecognition != null ? String(c.hasSpeechRecognition) : null],
            ["Speech Synth.", c => c.hasSpeechSynthesis != null ? String(c.hasSpeechSynthesis) : null],
            ["Payment Request", c => c.hasPaymentRequest != null ? String(c.hasPaymentRequest) : null],
            ["Notification", c => c.hasNotification != null ? String(c.hasNotification) : null],
            ["Push Manager", c => c.hasPushManager != null ? String(c.hasPushManager) : null],
            ["Crypto", c => c.hasCrypto != null ? String(c.hasCrypto) : null],
            ["Performance", c => c.hasPerformance != null ? String(c.hasPerformance) : null],
            ["Intersection Obs.", c => c.hasIntersectionObserver != null ? String(c.hasIntersectionObserver) : null],
            ["Resize Observer", c => c.hasResizeObserver != null ? String(c.hasResizeObserver) : null],
            ["Mutation Observer", c => c.hasMutationObserver != null ? String(c.hasMutationObserver) : null],
            ["RAF", c => c.hasRequestAnimationFrame != null ? String(c.hasRequestAnimationFrame) : null],
            ["Fetch", c => c.hasFetch != null ? String(c.hasFetch) : null],
            ["LocalStorage", c => c.hasLocalStorage != null ? String(c.hasLocalStorage) : null],
            ["SessionStorage", c => c.hasSessionStorage != null ? String(c.hasSessionStorage) : null],
            ["Shared Worker", c => c.hasSharedWorker != null ? String(c.hasSharedWorker) : null],
            ["WebAssembly", c => c.hasWebAssembly != null ? String(c.hasWebAssembly) : null],
            ["WebGPU", c => c.hasWebGPU != null ? String(c.hasWebGPU) : null],
            ["Compression Stream", c => c.hasCompressionStream != null ? String(c.hasCompressionStream) : null],
            ["File System Access", c => c.hasFileSystemAccess != null ? String(c.hasFileSystemAccess) : null],
            ["OPFS", c => c.hasOPFS != null ? String(c.hasOPFS) : null],
            ["Pointer Lock", c => c.hasPointerLock != null ? String(c.hasPointerLock) : null],
            ["Keyboard Lock", c => c.hasKeyboardLock != null ? String(c.hasKeyboardLock) : null],
            ["Idle Detection", c => c.hasIdleDetection != null ? String(c.hasIdleDetection) : null],
            ["Eye Dropper", c => c.hasEyeDropper != null ? String(c.hasEyeDropper) : null],
        ]
    },
    {
        title: "🎬 CODEC & MEDIA SUPPORT",
        id: "codecs",
        fields: [
            ["H.264 (MediaRecorder)", c => c.supportsH264 != null ? String(c.supportsH264) : null],
            ["VP9 (MediaRecorder)", c => c.supportsVP9 != null ? String(c.supportsVP9) : null],
            ["AV1 (MediaRecorder)", c => c.supportsAV1 != null ? String(c.supportsAV1) : null],
            ["HEVC (MediaRecorder)", c => c.supportsHEVC != null ? String(c.supportsHEVC) : null],
            ["Opus Audio", c => c.supportsOpus != null ? String(c.supportsOpus) : null],
            ["AAC Audio", c => c.supportsAAC != null ? String(c.supportsAAC) : null],
            ["Can Play H.264", c => c.canPlayH264],
            ["Can Play WebM", c => c.canPlayWebM],
            ["Can Play Ogg", c => c.canPlayOgg],
            ["Can Play HLS", c => c.canPlayHLS],
            ["Widevine DRM", c => c.hasWidevine != null ? String(c.hasWidevine) : null],
            ["EME (DRM)", c => c.hasEME != null ? String(c.hasEME) : null],
        ]
    },
    {
        title: "🥽 WEBXR / AR / VR",
        id: "webxr",
        fields: [
            ["WebXR", c => c.hasWebXR != null ? String(c.hasWebXR) : null],
            ["VR Support", c => c.hasVRSupport != null ? String(c.hasVRSupport) : null],
            ["AR Support", c => c.hasARSupport != null ? String(c.hasARSupport) : null],
            ["Inline XR", c => c.hasInlineXR != null ? String(c.hasInlineXR) : null],
        ]
    },
    {
        title: "🎨 CSS FEATURE SUPPORT",
        id: "css",
        fields: [
            ["CSS Grid", c => c.cssGrid != null ? String(c.cssGrid) : null],
            ["CSS Flexbox", c => c.cssFlexbox != null ? String(c.cssFlexbox) : null],
            ["Container Queries", c => c.cssContainerQueries != null ? String(c.cssContainerQueries) : null],
            [":has() Selector", c => c.cssHasSelector != null ? String(c.cssHasSelector) : null],
            ["Subgrid", c => c.cssSubgrid != null ? String(c.cssSubgrid) : null],
            ["@layer", c => c.cssCSSLayers != null ? String(c.cssCSSLayers) : null],
            ["Color P3", c => c.cssColorP3 != null ? String(c.cssColorP3) : null],
            ["OKLCH Color", c => c.cssColorOKLCH != null ? String(c.cssColorOKLCH) : null],
            ["Scroll Timeline", c => c.cssScrollTimeline != null ? String(c.cssScrollTimeline) : null],
            ["View Transitions", c => c.cssViewTransitions != null ? String(c.cssViewTransitions) : null],
            ["CSS Nesting", c => c.cssNesting != null ? String(c.cssNesting) : null],
            ["HDR (CSS)", c => c.cssHDR != null ? String(c.cssHDR) : null],
            ["Custom Props", c => c.cssCustomProps != null ? String(c.cssCustomProps) : null],
            ["Sticky Position", c => c.cssStickyPos != null ? String(c.cssStickyPos) : null],
            ["Aspect Ratio", c => c.cssAspectRatio != null ? String(c.cssAspectRatio) : null],
            ["Gap", c => c.cssGap != null ? String(c.cssGap) : null],
            ["Backdrop Filter", c => c.cssBackdropFilter != null ? String(c.cssBackdropFilter) : null],
            ["Clip Path", c => c.cssClipPath != null ? String(c.cssClipPath) : null],
            ["CSS Containment", c => c.cssContainment != null ? String(c.cssContainment) : null],
            ["Logical Props", c => c.cssLogicalProps != null ? String(c.cssLogicalProps) : null],
        ]
    },
    {
        title: "⚙️ WEBASSEMBLY",
        id: "wasm",
        fields: [
            ["WASM Supported", c => c.wasmSupported != null ? String(c.wasmSupported) : null],
            ["WASM SIMD", c => c.wasmSIMD != null ? String(c.wasmSIMD) : null],
            ["WASM Threads", c => c.wasmThreads != null ? String(c.wasmThreads) : null],
            ["WASM Exceptions", c => c.wasmExceptions != null ? String(c.wasmExceptions) : null],
            ["WASM GC", c => c.wasmGC != null ? String(c.wasmGC) : null],
        ]
    },
    {
        title: "📡 SENSORS",
        id: "sensors",
        fields: [
            ["Accelerometer", c => c.hasAccelerometer != null ? String(c.hasAccelerometer) : null],
            ["Gyroscope", c => c.hasGyroscope != null ? String(c.hasGyroscope) : null],
            ["Magnetometer", c => c.hasMagnetometer != null ? String(c.hasMagnetometer) : null],
            ["Ambient Light", c => c.hasAmbientLight != null ? String(c.hasAmbientLight) : null],
            ["Abs Orientation", c => c.hasAbsoluteOrientation != null ? String(c.hasAbsoluteOrientation) : null],
            ["Rel Orientation", c => c.hasRelativeOrientation != null ? String(c.hasRelativeOrientation) : null],
            ["Gravity Sensor", c => c.hasGravitySensor != null ? String(c.hasGravitySensor) : null],
            ["Gamepad Count", c => c.gamepadCount != null ? String(c.gamepadCount) : null],
            ["Pointer Capture", c => c.hasPointerCapture != null ? String(c.hasPointerCapture) : null],
            ["Pressure Sensor", c => c.hasPressure != null ? String(c.hasPressure) : null],
        ]
    },
    {
        title: "📄 PAGE CONTEXT",
        id: "page",
        fields: [
            ["Page URL", c => c.pageUrl],
            ["Hostname", c => c.pageHostname],
            ["Protocol", c => c.pageProtocol],
            ["Path", c => c.pagePathname],
            ["Query", c => c.pageSearch],
            ["Hash", c => c.pageHash],
        ]
    },
    {
        title: "🧬 UA CLIENT HINTS",
        id: "ua_hints",
        fields: [
            ["Architecture", c => c.uaArchitecture],
            ["Bitness", c => c.uaBitness],
            ["Model", c => c.uaModel],
            ["Platform Version", c => c.uaPlatformVersion],
            ["Full Version", c => c.uaFullVersion],
            ["Full Version List", c => c.uaFullVersionList],
            ["WoW64", c => c.uaWow64 != null ? String(c.uaWow64) : null],
            ["Mobile", c => c.uaMobile != null ? String(c.uaMobile) : null],
        ]
    },
    {
        title: "⏱️ PERFORMANCE BREAKDOWN",
        id: "perf_breakdown",
        fields: [
            ["DNS Lookup", c => c.dnsLookupTime != null ? `${c.dnsLookupTime} ms` : null],
            ["TCP Connection", c => c.tcpConnectionTime != null ? `${c.tcpConnectionTime} ms` : null],
            ["Server Response", c => c.serverResponseTime != null ? `${c.serverResponseTime} ms` : null],
            ["Page Download", c => c.pageDownloadTime != null ? `${c.pageDownloadTime} ms` : null],
            ["Browser Render", c => c.browserRenderTime != null ? `${c.browserRenderTime} ms` : null],
            ["Redirect Time", c => c.redirectTime != null ? `${c.redirectTime} ms` : null],
            ["TLS Handshake", c => c.tlsHandshakeTime != null ? `${c.tlsHandshakeTime} ms` : null],
        ]
    },
    {
        title: "📐 SCREEN EXTRAS",
        id: "screen_extras",
        fields: [
            ["Aspect Ratio", c => c.screenAspectRatio],
            ["Viewport Width", c => c.viewportWidth],
            ["Viewport Height", c => c.viewportHeight],
        ]
    },
    {
        title: "🔧 ENVIRONMENT",
        id: "env",
        fields: [
            ["Speech Voices", c => c.speechVoicesCount],
            ["Popup Blocked", c => c.popupBlocker != null ? (c.popupBlocker ? "Yes" : "No") : null],
            ["WebSocket Allowed", c => c.webSocketAllowed != null ? String(c.webSocketAllowed) : null],
            ["Page First Visible", c => c.pageFirstVisible ? toIST(c.pageFirstVisible) : null],
            ["Page Request Time", c => c.pageRequestTime ? toIST(c.pageRequestTime) : null],
            ["Device Orientation", c => c.hasDeviceOrientation != null ? String(c.hasDeviceOrientation) : null],
            ["Device Motion", c => c.hasDeviceMotion != null ? String(c.hasDeviceMotion) : null],
            ["Has Mouse", c => c.hasMouse != null ? String(c.hasMouse) : null],
            ["Has Touchscreen", c => c.hasTouchscreen != null ? String(c.hasTouchscreen) : null],
        ]
    },
];

function toIST(dateInput) {
    if (!dateInput) return "-";
    const date = dateInput?.toMillis ? new Date(dateInput.toMillis()) : new Date(dateInput);
    return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
}

// --- Enhanced Signal Intelligence Summary ---
// --- Narrative Signal Intelligence Summary ---
function generateSingleLinkSummary(captures) {
    if (!captures || captures.length === 0) return "No tracking data available for this link yet.";

    let summary = `🚨 OFFICIAL FORENSIC INVESTIGATION BRIEFING\n`;
    summary += `CASE RECORD FILE · TRAXELON INTEL SYSTEM\n`;
    summary += `Total Intercepted Target Signals: ${captures.length}\n`;
    summary += `============================================================\n\n`;

    captures.forEach((c, index) => {
        const time = toIST(c.capturedAt);
        const deviceType = c.isMobile ? "mobile handset" : "desktop/workstation";

        summary += `📡 DIGITAL FORENSIC REPORT - SIGNAL #${index + 1}\n`;
        summary += `------------------------------------------------------------\n`;

        // 1. Time & Intercept Metadata
        summary += `[I] TIMING & SIGNAL ACQUISITION\n`;
        summary += `- Interception Timestamp: ${time} (Kolkata/IST)\n`;
        summary += `- Connection Status: ACTIVE / CAPTURED\n\n`;

        // 2. IP Address & Provider Details
        summary += `[II] IP ROUTING & NETWORK FINGERPRINT\n`;
        summary += `- Target IP Address: ${c.ip || '[REDACTED]'}\n`;
        summary += `- Internet Service Provider (ISP): ${c.isp || 'N/A'}\n`;
        summary += `- Network Connection Type: ${c.connectionType || 'Broadband/Cellular Network'}\n`;
        summary += `- Routing Integrity: ${c.isVpn || c.isProxy ? "VPN/Proxy Masking Detected (Identity Concealment Suspected)" : "Direct Commercial/Residential Connection (No active masking detected)"}\n\n`;

        // 3. Location Triangulation & GPS Specs
        summary += `[III] GEOGRAPHIC LOGS & COORDINATES\n`;
        summary += `- Estimated Geolocation: ${c.city || 'N/A'}, ${c.region || 'N/A'}, ${c.country || 'India'}\n`;
        if (c.gpsLat && c.gpsLon) {
            summary += `- Precise GPS Acquisition: Latitude ${c.gpsLat.toFixed(6)}, Longitude ${c.gpsLon.toFixed(6)}\n`;
            summary += `- Acquisition Source: Target-supplied GPS API (High Confidence)\n\n`;
        } else {
            summary += `- Precise GPS Acquisition: Coordinates Unavailable (GPS permission denied)\n`;
            summary += `- Triangulation Source: Network-derived IP Geo IP-Mapping (Medium/Low Confidence)\n\n`;
        }

        // 4. Client Hardware & System Fingerprint
        summary += `[IV] HARDWARE & USER AGENT SPECIFICATIONS\n`;
        summary += `- Device Classification: ${deviceType}\n`;
        summary += `- Operating System (OS): ${c.os || 'N/A'}\n`;
        summary += `- Client Web Browser: ${c.browser || 'N/A'}\n`;
        summary += `- Screen Resolution: ${c.screenWidth && c.screenHeight ? `${c.screenWidth}x${c.screenHeight}` : 'N/A'} px\n`;
        if (c.batteryLevel !== undefined && c.batteryLevel !== null) {
            summary += `- Battery Metrics: ${c.batteryLevel}% (${c.isCharging ? 'Plugged/Charging' : 'Unplugged/Discharging'})\n`;
        }
        if (c.cpuCores) {
            summary += `- CPU Hardware Profiling: ${c.cpuCores} Processing Cores Detected\n`;
        }

        summary += `\n[FORENSIC STATUS: VERIFIED AND INDEXED INTO EVIDENCE LOGS]\n`;
        summary += `------------------------------------------------------------\n\n`;
    });

    summary += `============================================================\n`;
    summary += `END OF FORENSIC BRIEFING REPORT`;

    return summary;
}

export default function LinkGenerator() {
    const { currentUser, userProfile, loading: authLoading } = useAuth();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";

    useEffect(() => {
        document.title = "URL Tracking Link Generator — Traxelon Covert Capture";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute("content", "Generate covert, audit-ready tracking links for authorized law enforcement investigations. Traxelon URL capture with device fingerprinting, geolocation, and full audit logging.");
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute("href", "https://traxelon.com/generate");
        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl) ogUrl.setAttribute("content", "https://traxelon.com/generate");
    }, []);

    function generateCloakedUrl(originalUrl, token, frontendUrl) {
        try {
            const prefix = process.env.REACT_APP_ROUTE_PREFIX || "r";
            const parsed = new URL(originalUrl);
            let hostname = parsed.hostname;
            hostname = hostname.replace(/^www\./, '');
            const path = parsed.pathname === '/' ? '' : parsed.pathname;
            const search = parsed.search || '';
            const hash = parsed.hash || '';

            const frontendParsed = new URL(frontendUrl);
            const frontendHost = frontendParsed.host;

            return `${frontendParsed.protocol}//${hostname}@${frontendHost}/${prefix}/${token}${path}${search}${hash}`;
        } catch {
            return null;
        }
    }
    const location = useLocation();
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [activeSummaryText, setActiveSummaryText] = useState("");
    const [isBotOpen, setIsBotOpen] = useState(false);
    const [copied, setCopied] = useState(null);
    const [captures, setCaptures] = useState([]);
    const [expandedCapture, setExpandedCapture] = useState(null);
    const [generatedLink, setGeneratedLink] = useState(() => {
        const saved = sessionStorage.getItem("traxelon_last_link");
        return location.state || (saved ? JSON.parse(saved) : null);
    });
    const [currentToken] = useState(() => {
        const saved = sessionStorage.getItem("traxelon_last_link");
        return location.state?.token || (saved ? JSON.parse(saved).token : null);
    });
    const [selectedDomain, setSelectedDomain] = useState("tinyurl");
    const [customShortUrl, setCustomShortUrl] = useState(null);
    const [shortening, setShortening] = useState(false);
    const [linkDocRef, setLinkDocRef] = useState(null);
    const [usePolling, setUsePolling] = useState(false);

    useEffect(() => {
        if (authLoading) return;
        if (!currentUser) return;
        if (!currentToken || typeof currentToken !== "string") return;

        const linkQuery = query(
            collection(db, "trackingLinks"),
            where("token", "==", currentToken),
            limit(1)
        );

        const unsubLink = onSnapshot(linkQuery, (snap) => {
            if (snap.empty) return;
            const doc = snap.docs[0];
            const linkData = doc.data();
            if (linkData.createdAt) {
                setGeneratedLink(prev => prev ? {
                    ...prev,
                    createdAt: linkData.createdAt,
                    tinyUrl: linkData.tinyUrl || prev.tinyUrl,
                    isGdUrl: linkData.isGdUrl || prev.isGdUrl,
                    tlyUrl: linkData.tlyUrl || prev.tlyUrl,
                    shortIoUrl: linkData.shortIoUrl || prev.shortIoUrl,
                    cloakUrl: linkData.cloakUrl || prev.cloakUrl,
                } : prev);
            }
            setLinkDocRef(doc.ref);
        }, (err) => {
            console.error("❌ trackingLinks listener error:", err.code, err.message);
        });

        return unsubLink;
    }, [currentToken, currentUser, authLoading]);

    useEffect(() => {
        if (!linkDocRef) return;
        const capturesColRef = collection(linkDocRef, "captures");
        const unsubCaptures = onSnapshot(capturesColRef, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => new Date(b.capturedAt) - new Date(a.capturedAt));
            setCaptures(data);
            setUsePolling(false);
        }, (err) => {
            console.error("❌ captures subcollection error:", err.code, err.message);
            if (err.code === "permission-denied" || err.code === "unauthenticated") {
                setUsePolling(true);
            }
        });
        return unsubCaptures;
    }, [linkDocRef]);

    useEffect(() => {
        if (!usePolling || !currentToken || !currentUser) return;
        const fetchCaptures = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/links/captures?token=${currentToken}&uid=${currentUser.uid}`);
                if (res.ok) {
                    const data = await res.json();
                    setCaptures(data.captures || []);
                }
            } catch (err) {
                console.error("❌ Polling error:", err.message);
            }
        };
        fetchCaptures();
        const interval = setInterval(fetchCaptures, 5000);
        return () => clearInterval(interval);
    }, [usePolling, currentToken, currentUser, BACKEND_URL]);

    function copyToClipboard(text, key) {
        navigator.clipboard.writeText(text);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    }

    return (
        <div className="min-h-screen bg-surface text-text-primary">
            <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-20 pointer-events-none" />

            {generatedLink && (
                <section className="relative pt-24 pb-10 px-4">
                    <div className="max-w-7xl mx-auto">
                        <div className="bg-surface-elevated border border-surface-border rounded-2xl overflow-hidden shadow-card">
                            <div className="border-b border-surface-border px-6 py-4 flex items-center justify-between">
                                <h2 className="font-display text-xl tracking-wider">
                                    LINK <span className="text-primary">INFORMATION</span>
                                </h2>
                                {/* <span className="font-body text-xs text-text-muted italic">All links stay active permanently</span> */}
                                <a href="/dashboard" className="flex items-center gap-1.5 font-body text-xs text-primary hover:underline flex-shrink-0">
                                    View in Dashboard <ChevronRight className="w-3 h-3" />
                                </a>
                            </div>
                            <div className="divide-y divide-surface-border">
                                <InfoRow label="Original URL">
                                    <span className="font-mono text-sm text-text-secondary break-all">{generatedLink.originalUrl}</span>
                                </InfoRow>
                                <InfoRow label="Tracking URL">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="font-mono text-sm text-primary break-all flex-1 min-w-0">{generatedLink.shortUrl}</span>
                                        <button onClick={() => copyToClipboard(generatedLink.shortUrl, "short")} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-lg font-body text-xs hover:bg-primary/20 transition-colors flex-shrink-0">
                                            {copied === "short" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                            {copied === "short" ? "Copied!" : "Copy"}
                                        </button>
                                    </div>
                                </InfoRow>

                                <InfoRow label="Tracking Code">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="font-mono text-sm text-primary break-all flex-1 min-w-0">
                                            {generatedLink.token}
                                        </span>

                                        <button
                                            onClick={() => copyToClipboard(generatedLink.token, "token")}
                                            className="flex items-center gap-1.5 px-3 py-1.5 border border-surface-border text-text-secondary rounded-lg font-body text-xs hover:border-primary hover:text-primary transition-colors flex-shrink-0"
                                        >
                                            {copied === "token" ? (
                                                <>
                                                    <Check className="w-3 h-3" /> Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-3 h-3" /> Copy
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </InfoRow>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-6 py-4 border-t border-surface-border">
                                    <div className="font-body text-xs text-text-muted uppercase tracking-wider w-36 flex-shrink-0">Short Link</div>
                                    <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
                                        <span className={`font-mono text-sm break-all flex-1 min-w-0 ${shortening ? "text-yellow-400 animate-pulse" : customShortUrl?.startsWith("FAILED") ? "text-red-400" : "text-primary"}`}>
                                            {shortening ? "Generating..." : customShortUrl?.startsWith("FAILED") ? customShortUrl.replace("FAILED: ", "") : customShortUrl || (selectedDomain === "tinyurl" ? generatedLink.tinyUrl : null) || generatedLink.tinyUrl || generatedLink.cloakUrl || "Generating..."}
                                        </span>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <select value={selectedDomain} onChange={async (e) => {
                                                const prov = e.target.value;
                                                setSelectedDomain(prov);
                                                setCustomShortUrl(null);

                                                if (prov === "cloak") {
                                                    const frontendUrl = window.location.origin;
                                                    const cloak = generateCloakedUrl(generatedLink.originalUrl, generatedLink.token, frontendUrl);
                                                    if (cloak) setCustomShortUrl(cloak);
                                                    else setCustomShortUrl("FAILED: Could not generate cloaked URL");
                                                    return;
                                                }

                                                // Reuse pre-generated URLs if available
                                                if (prov === "tinyurl" && generatedLink.tinyUrl) return;
                                                if (prov === "isgd" && generatedLink.isGdUrl) { setCustomShortUrl(generatedLink.isGdUrl); return; }
                                                if (prov === "tly" && generatedLink.tlyUrl) { setCustomShortUrl(generatedLink.tlyUrl); return; }
                                                if (prov === "shortio" && generatedLink.shortIoUrl) { setCustomShortUrl(generatedLink.shortIoUrl); return; }
                                                if (prov === "cloakurl" && generatedLink.cloakUrl) { setCustomShortUrl(generatedLink.cloakUrl); return; }
                                                setShortening(true);
                                                try {
                                                    // All providers routed through backend to avoid CORS blocks
                                                    const r = await fetch(BACKEND_URL + "/api/links/shorten-url", {
                                                        method: "POST",
                                                        headers: { "Content-Type": "application/json" },
                                                        body: JSON.stringify({ url: generatedLink.trackingUrl, provider: prov }),
                                                    });
                                                    const data = await r.json();
                                                    if (data.shortUrl) {
                                                        setCustomShortUrl(data.shortUrl);
                                                    } else {
                                                        setCustomShortUrl("FAILED: " + (data.error || "Provider unavailable"));
                                                    }
                                                } catch (err) {
                                                    setCustomShortUrl("FAILED: Network error");
                                                }
                                                setShortening(false);
                                            }} className="bg-surface-elevated border border-primary/40 text-text-primary font-body text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-primary transition-colors cursor-pointer">
                                                <option value="cloak">Cloak (looks like original)</option>
                                                <option value="tinyurl">tinyurl.com (free)</option>
                                                <option value="isgd">is.gd (free)</option>
                                                <option value="cleanuri">cleanuri.com (free)</option>
                                                <option value="dagd">da.gd (free)</option>
                                                <option value="linkshrink">linkshrink.dev (free)</option>
                                            </select>
                                            <button onClick={() => {
                                                const url = customShortUrl && customShortUrl !== "FAILED" ? customShortUrl : selectedDomain === "cloak" ? generatedLink.cloakUrl || customShortUrl : selectedDomain === "tinyurl" ? generatedLink.tinyUrl : selectedDomain === "isgd" ? generatedLink.isGdUrl : selectedDomain === "tly" ? generatedLink.tlyUrl : selectedDomain === "shortio" ? generatedLink.shortIoUrl : generatedLink.tinyUrl;
                                                if (url) copyToClipboard(url, "shortDomain");
                                            }} disabled={shortening || customShortUrl === "FAILED"} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-lg font-body text-xs hover:bg-primary/20 transition-colors flex-shrink-0">
                                                {copied === "shortDomain" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                                {copied === "shortDomain" ? "Copied!" : "Copy"}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <InfoRow label="Pixel URL">
                                    <div className="flex flex-col gap-2 flex-1 min-w-0">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <span className="font-mono text-[10px] sm:text-xs text-text-muted break-all flex-1 min-w-0">
                                                {`${BACKEND_URL}/api/links/pixel/${generatedLink.token}.gif`}
                                            </span>
                                            <button onClick={() => copyToClipboard(`${BACKEND_URL}/api/links/pixel/${generatedLink.token}.gif`, "pixel")}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-lg font-body text-xs hover:bg-primary/20 transition-colors flex-shrink-0">
                                                {copied === "pixel" ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-yellow-400/80 font-body flex items-center gap-1.5 leading-relaxed bg-yellow-400/5 px-3 py-2 rounded-lg border border-yellow-400/10">
                                            <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                            Note: Real IP/Location may be masked by Email Proxies (Gmail/Outlook/Apple). We've filtered out preview hits for accuracy.
                                        </p>
                                    </div>
                                </InfoRow>
                                <InfoRow label="Created">
                                    <span className="font-mono text-sm text-text-secondary">{toIST(generatedLink.createdAt)}</span>
                                </InfoRow>
                                <InfoRow label="Credits Left">
                                    <span className="font-mono text-sm text-primary">{userProfile?.credits ?? 0} credits</span>
                                </InfoRow>
                            </div>
                            <div className="px-6 py-4 bg-surface border-t border-surface-border flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                                <p className="font-body text-xs text-text-muted">
                                    Share the <span className="text-primary font-semibold">New URL</span> to track clicks, or embed the <span className="text-primary font-semibold">Pixel URL</span> in an email to track opens — no click needed.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {generatedLink && (
                <section className="px-4 pb-10">
                    <div className="max-w-7xl mx-auto">
                        <div className="bg-surface-elevated border border-surface-border rounded-2xl overflow-hidden shadow-card">
                            <div className="border-b border-surface-border px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <h2 className="font-display text-xl tracking-wider">
                                        RESULTS: <span className="text-primary">{captures.length}</span>
                                    </h2>
                                    {captures.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <span className="flex items-center gap-1 font-mono text-xs text-green-400">
                                                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
                                                LIVE
                                            </span>

                                            {/* --- THE SUMMARIZE BUTTON --- */}
                                            <button
                                                onClick={() => {
                                                    const text = generateSingleLinkSummary(captures);
                                                    setActiveSummaryText(text);
                                                    setShowSummaryModal(true);
                                                }}
                                                className="flex items-center gap-1.5 px-3 py-1 bg-primary/10 border border-primary/20 text-primary rounded-lg font-body text-[10px] uppercase font-bold hover:bg-primary/20 transition-all tracking-tighter"
                                            >
                                                <Sparkles className="w-3 h-3" /> Summarize
                                            </button>
                                            <button
                                                onClick={() => setIsBotOpen(true)}
                                                className="flex items-center gap-1.5 px-3 py-1 bg-primary/10 border border-primary/20 text-primary rounded-lg font-body text-[10px] uppercase font-bold hover:bg-primary/20 transition-all"
                                            >
                                                <Shield className="w-3 h-3" /> AI Bot
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <span className="font-body text-xs text-text-muted">Updates in real time</span>
                            </div>
                            {captures.length === 0 ? (
                                <div className="px-6 py-12 text-center">
                                    <Eye className="w-10 h-10 text-text-muted mx-auto mb-3" />
                                    <p className="font-body text-sm text-text-muted">No captures yet.</p>
                                    <p className="font-body text-xs text-text-muted mt-1">Share the link above — results appear here the moment someone clicks it.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-surface-border">
                                    {captures.map((capture, i) => (
                                        <CaptureRow key={capture.id || i} capture={capture} index={captures.length - i} expanded={expandedCapture === i} onToggle={() => setExpandedCapture(expandedCapture === i ? null : i)} generatedLink={generatedLink} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )
            }

            <Footer />

            {showSummaryModal && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[999] flex items-center justify-center p-4">
                    <div className="bg-surface-elevated border border-primary/30 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl">
                        <div className="border-b border-surface-border px-6 py-4 flex items-center justify-between bg-primary/5">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-primary" />
                                <h2 className="font-display text-lg tracking-widest uppercase">Intelligence <span className="text-primary">Briefing</span></h2>
                            </div>
                            <button onClick={() => setShowSummaryModal(false)} className="text-text-muted hover:text-white transition-colors text-xl font-bold">×</button>
                        </div>
                        <div className="p-6 max-h-[70vh] overflow-y-auto">
                            <pre className="font-mono text-sm leading-relaxed text-text-secondary whitespace-pre-wrap bg-surface p-4 rounded-xl border border-surface-border">
                                {activeSummaryText}
                            </pre>
                        </div>
                        <div className="p-4 border-t border-surface-border bg-surface flex justify-end">
                            <button
                                onClick={() => setShowSummaryModal(false)}
                                className="px-6 py-2 bg-primary text-surface font-display text-sm font-bold rounded-lg hover:bg-primary-dark transition-all"
                            >
                                ACKNOWLEDGE
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ForensicBot
                captures={captures}
                isOpen={isBotOpen}
                onClose={() => setIsBotOpen(false)}
            />

        </div >
    );
}




// ── Capture row component ─────────────────────────────────────────────────────
function CaptureRow({ capture, index, expanded, onToggle, generatedLink }) {
    const hasGPS = capture.gpsLat && capture.gpsLon;

    async function exportToPDF() {
        const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const pageW = 210;
        const pageH = 297;
        const margin = 16;
        const colW = (pageW - margin * 2 - 8) / 2; // gap between columns
        let y = margin;

        const COLORS = {
            bg: [10, 18, 32], primary: [0, 212, 255], white: [255, 255, 255],
            muted: [120, 140, 160], sectionBg: [16, 28, 48], border: [30, 50, 80], label: [100, 130, 160],
        };

        const addFooter = () => {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.muted);
            doc.text("© 2026 Traxelon · A project of TorSecure.", pageW / 2, pageH - 6, { align: "center" });
        };

        const addPage = () => {
            addFooter();
            doc.addPage();
            y = margin;
            doc.setFillColor(...COLORS.bg);
            doc.rect(0, 0, pageW, pageH, "F");
        };

        const checkY = (needed = 10) => {
            if (y + needed > pageH - margin - 10) addPage();
        };

        // ── Background ──
        doc.setFillColor(...COLORS.bg);
        doc.rect(0, 0, pageW, pageH, "F");

        // ── Header bar ──
        const headerH = 20;
        doc.setFillColor(...COLORS.primary);
        doc.rect(0, 0, pageW, headerH, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(...COLORS.bg);
        doc.text("TRAXELON", margin, 13);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("CAPTURE REPORT", margin + 36, 13);

        doc.setTextColor(...COLORS.bg);
        doc.setFontSize(8);
        doc.text(
            `Generated: ${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}`,
            pageW - margin, 13, { align: "right" }
        );

        y = headerH + 8; // breathing room after header

        // ── Original / Tracking URL card ──
        if (generatedLink) {
            const cardH = 32;
            checkY(cardH + 4);

            doc.setFillColor(...COLORS.sectionBg);
            doc.roundedRect(margin, y, pageW - margin * 2, cardH, 2, 2, "F");

            // Original URL
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(...COLORS.primary);
            doc.text("ORIGINAL URL", margin + 5, y + 7);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(...COLORS.white);
            const origLines = doc.splitTextToSize(generatedLink.originalUrl || "—", pageW - margin * 2 - 10);
            doc.text(origLines, margin + 5, y + 13);

            // Tracking URL
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(...COLORS.primary);
            doc.text("TRACKING URL", margin + 5, y + 22);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.muted);
            doc.text(generatedLink.shortUrl || generatedLink.trackingUrl || "—", margin + 5, y + 28);

            y += cardH + 10; // gap after card
        }

        // ── Captures ──
        [capture].forEach((capture, captureIndex) => {

            // Capture header pill
            checkY(14);
            doc.setFillColor(...COLORS.primary);
            doc.roundedRect(margin, y, pageW - margin * 2, 11, 1.5, 1.5, "F");

            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor(...COLORS.bg);
            const hasGPS = capture.gpsLat && capture.gpsLon;
            const captureLabel = [
                `CAPTURE #${captureIndex + 1}`,
                hasGPS ? "● GPS" : "● IP",
                capture.capturedAt
                    ? new Date(capture.capturedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                    : ""
            ].filter(Boolean).join("    ");
            doc.text(captureLabel, margin + 4, y + 7.5);
            y += 16; // gap after pill

            // ── Sections ──
            CAPTURE_SECTIONS.forEach(section => {
                if (capture.captureType === "pixel") {
                    const allowedPixelSections = ["network", "headers", "ip_location", "browser_details", "timezone", "page", "ua_hints"];
                    if (!allowedPixelSections.includes(section.id)) return;
                }

                const rows = section.fields
                    .map(([label, getValue]) => ({ label, value: getValue(capture) }))
                    .filter(r => r.value != null && r.value !== "" && r.value !== "null");

                if (rows.length === 0) return;

                // Section title + divider
                checkY(12);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor(...COLORS.primary);
                // eslint-disable-next-line no-control-regex
                const cleanTitle = section.title.replace(/[^\x00-\x7F]/g, "").trim();
                doc.text(cleanTitle, margin, y);

                // GPS map link (right-aligned on same line as title)
                if (section.id === "gps_location" && capture.gpsLat && capture.gpsLon) {
                    const mapUrl = `https://www.google.com/maps?q=${capture.gpsLat},${capture.gpsLon}`;
                    const linkText = ">> VIEW IN GOOGLE MAPS";
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(7.5);
                    doc.setTextColor(...COLORS.primary);
                    const linkW = doc.getTextWidth(linkText);
                    const linkX = pageW - margin - linkW;
                    doc.textWithLink(linkText, linkX, y, { url: mapUrl });
                    doc.setLineWidth(0.15);
                    doc.setDrawColor(...COLORS.primary);
                    doc.line(linkX, y + 0.6, pageW - margin, y + 0.6);
                }

                // Divider line
                doc.setDrawColor(...COLORS.primary);
                doc.setLineWidth(0.25);
                doc.line(margin, y + 2, pageW - margin, y + 2);
                y += 7; // gap before rows

                // ── Two-column rows ──
                let col = 0;
                let rowStartY = y;

                rows.forEach(({ label, value }, i) => {
                    const x = col === 0 ? margin : margin + colW + 8;
                    const availW = colW - 4;

                    // Estimate needed height
                    doc.setFontSize(9);
                    const wrapped = doc.splitTextToSize(String(value), availW);
                    const cellH = 5 + wrapped.length * 4.5 + 5; // label + value lines + bottom pad

                    if (col === 0) {
                        checkY(cellH);
                        rowStartY = y;
                    }

                    // Label
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(7);
                    doc.setTextColor(...COLORS.label);
                    doc.text(label.toUpperCase(), x, y);

                    // Value
                    doc.setFont("helvetica", "normal");
                    doc.setFontSize(9);
                    doc.setTextColor(...COLORS.white);
                    doc.text(wrapped, x, y + 5);

                    if (col === 0) {
                        col = 1;
                    } else {
                        col = 0;
                        // Move down by the taller of the two cells
                        const leftH = 5 + doc.splitTextToSize(String(rows[i - 1]?.value ?? ""), availW).length * 4.5 + 5;
                        const rightH = cellH;
                        y = rowStartY + Math.max(leftH, rightH) + 2;
                    }
                });

                // Flush last odd row if any
                if (col === 1) {
                    y += 5 + 4.5 + 5 + 2; // single row height fallback
                }

                y += 6; // gap between sections
            });

            y += 4; // gap between captures
        });

        addFooter();
        doc.save(`Traxelon-captures-${generatedLink?.token || "report"}-${Date.now()}.pdf`);
    } 

    return (
        <div>
            <div className="flex items-center gap-3 px-6 py-4 hover:bg-surface cursor-pointer" onClick={onToggle}>
                <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: "rgba(0,212,255,0.12)", color: "#00d4ff", border: "1px solid rgba(0,212,255,0.3)" }}>
                    #{index}
                </span>
                {capture.captureType === "pixel" && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-mono border flex-shrink-0 bg-purple-500/10 text-purple-400 border-purple-500/20">
                        👁️ PIXEL
                    </span>
                )}
                {capture.captureType !== "pixel" && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-mono border flex-shrink-0 ${hasGPS ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-primary/10 text-primary border-primary/20"}`}>
                        {hasGPS ? "📍 GPS" : "🌐 IP"}
                    </span>
                )}
                <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                        <div className="font-body text-xs text-text-muted uppercase tracking-wider mb-0.5">Date / Time</div>
                        <div className="font-mono text-xs text-text-primary truncate">{toIST(capture.capturedAt)}</div>
                    </div>
                    <div>
                        <div className="font-body text-xs text-text-muted uppercase tracking-wider mb-0.5">IP / Provider</div>
                        <div className="font-mono text-xs text-text-primary truncate flex items-center gap-1.5">
                            {capture.ip || "-"}
                            {capture.proxyLabel && (
                                <span className="px-1.5 py-0.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded text-[10px] leading-tight font-bold">
                                    {capture.proxyLabel}
                                </span>
                            )}
                            {capture.emailClient && capture.captureType === "pixel" && (
                                <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded text-[10px] leading-tight font-bold">
                                    {capture.emailClient}
                                </span>
                            )}
                            {capture.isBot && (
                                <span className="px-1.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded text-[10px] leading-tight font-bold">
                                    BOT
                                </span>
                            )}
                        </div>
                        <div className="font-body text-xs text-text-muted truncate">{capture.isp || ""}</div>
                    </div>
                    <div className="hidden md:block">
                        <div className="font-body text-xs text-text-muted uppercase tracking-wider mb-0.5">Location</div>
                        <div className="font-mono text-xs text-text-primary truncate">{hasGPS ? (capture.gpsCity || capture.city || "-") : (capture.city || "-")}</div>
                        <div className="font-body text-xs text-text-muted truncate">{capture.country || ""}</div>
                    </div>
                    <div className="hidden md:block">
                        <div className="font-body text-xs text-text-muted uppercase tracking-wider mb-0.5">Device</div>
                        <div className="font-mono text-xs text-text-primary truncate">{capture.device || "-"}</div>
                        <div className="font-body text-xs text-text-muted truncate">{capture.browser || ""} · {capture.os || ""}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); exportToPDF(); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-lg font-body text-xs hover:bg-primary/20 transition-colors">
                        <Download className="w-3 h-3" /> Export PDF
                    </button>
                    <div className="text-text-muted">{expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</div>
                </div>
            </div>

            {expanded && (
                <div className="px-6 pb-6 bg-surface">
                    <div className="rounded-xl p-4" style={{ background: "rgba(10, 22, 40, 0.85)", border: "1.5px solid rgba(0, 212, 255, 0.25)" }}>

                        {/* ── PIXEL-SPECIFIC EMAIL INTELLIGENCE VIEW ── */}
                        {capture.captureType === "pixel" && (
                            <div className="space-y-4">

                                {/* Email Interaction */}
                                <DetailSection title="📧 EMAIL INTERACTION">
                                    <DataRow label="Event" value="Email Opened" />
                                    <DataRow label="Email Client" value={capture.emailClient} />
                                    <DataRow label="Mail Proxy" value={capture.proxyLabel} />
                                    <DataRow label="Is Email Proxy" value={capture.isEmailProxy != null ? (capture.isEmailProxy ? "Yes" : "No") : null} />
                                    <DataRow label="Times Opened" value={capture.openCount ?? 1} />
                                    <DataRow label="First Opened At" value={capture.capturedAt ? new Date(capture.capturedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "full", timeStyle: "medium" }) : null} />
                                    <DataRow label="Last Opened At" value={capture.lastOpenedAt ? new Date(capture.lastOpenedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "full", timeStyle: "medium" }) : null} />
                                    <DataRow label="Referrer URL" value={capture.referrer} />
                                    <DataRow label="Unique Identifier" value={capture.ip ? `${capture.ip}-${capture.capturedAt}` : null} />

                                    {/* ── Open History Timeline ── */}
                                    {capture.openHistory && capture.openHistory.length > 0 && (
                                        <div className="col-span-2 mt-3">
                                            <div className="font-body text-xs text-text-muted uppercase tracking-wider mb-2">Open History</div>
                                            <div className="space-y-1.5 pl-2 border-l-2 border-primary/30">
                                                {capture.openHistory.map((ts, i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 -ml-[5px]" />
                                                        <span className="font-mono text-xs text-text-secondary">
                                                            #{i + 1} — {new Date(ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "medium" })}
                                                        </span>
                                                        {i === 0 && (
                                                            <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded font-mono">FIRST OPEN</span>
                                                        )}
                                                        {i === capture.openHistory.length - 1 && capture.openHistory.length > 1 && (
                                                            <span className="text-[10px] px-1.5 py-0.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded font-mono">LATEST</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </DetailSection>

                                {/* Technical & Environmental */}
                                <DetailSection title="🌐 NETWORK & IP">
                                    <DataRow label="IP Address" value={capture.ip} />
                                    <DataRow label="ISP" value={capture.isp} />
                                    <DataRow label="Organisation" value={capture.org} />
                                    <DataRow label="Proxy Detected" value={capture.isProxy ? "Yes" : "No"} />
                                    <DataRow label="VPN/Datacenter" value={capture.isHosting ? "Yes — Likely VPN/Datacenter" : "No"} />
                                    <DataRow label="Mobile Network" value={capture.isMobileNetwork != null ? String(capture.isMobileNetwork) : null} />
                                </DetailSection>

                                {/* IP Location */}
                                <DetailSection title="📡 IP LOCATION (APPROXIMATE)">
                                    <DataRow label="City" value={capture.city} />
                                    <DataRow label="Region" value={capture.region} />
                                    <DataRow label="Country" value={capture.country} />
                                    <DataRow label="Country Code" value={capture.countryCode} />
                                    <DataRow label="ZIP / Postal" value={capture.zip} />
                                    <DataRow label="Coordinates" value={capture.lat ? `${capture.lat}, ${capture.lon}` : null} />
                                    <DataRow label="Timezone" value={capture.timezone} />
                                    {capture.lat && capture.lon && (
                                        <div className="col-span-2 mt-2">
                                            <div className="rounded-xl overflow-hidden border border-surface-border mb-2" style={{ height: 160 }}>
                                                <iframe title={`pixel-map-${index}`} width="100%" height="100%" frameBorder="0"
                                                    src={`https://maps.google.com/maps?q=${capture.lat},${capture.lon}&z=10&output=embed`} allowFullScreen />
                                            </div>
                                            <p className="font-body text-xs text-yellow-400/80 mt-1">
                                                ⚠️ Note: If email client is Gmail, this location is Google's proxy server (Mountain View, CA), not the suspect's actual location. For Outlook/Apple Mail direct opens, this reflects the real IP.
                                            </p>
                                        </div>
                                    )}
                                </DetailSection>

                                {/* User-Agent / Device */}
                                <DetailSection title="📱 DEVICE (FROM USER-AGENT)">
                                    <DataRow label="Device Type" value={capture.device} />
                                    <DataRow label="OS" value={capture.os} />
                                    <DataRow label="Browser / App" value={capture.browser} />
                                    <DataRow label="User-Agent String" value={capture.userAgent} />
                                </DetailSection>

                                {/* Timestamp */}
                                <DetailSection title="⏱️ TIMESTAMP">
                                    <DataRow label="Captured At (UTC)" value={capture.capturedAt ? new Date(capture.capturedAt).toUTCString() : null} />
                                    <DataRow label="Captured At (IST)" value={capture.capturedAt ? new Date(capture.capturedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : null} />
                                    <DataRow label="Unix Timestamp" value={capture.capturedAt ? new Date(capture.capturedAt).getTime() : null} />
                                </DetailSection>

                            </div>
                        )}

                        {/* ── STANDARD LINK CAPTURE VIEW ── */}
                        {capture.captureType !== "pixel" && CAPTURE_SECTIONS.map((section) => {
                            // Filter fields to those that actually have data
                            const activeFields = section.fields
                                .map(([label, getValue]) => ({ label, value: getValue(capture) }))
                                .filter(f => f.value != null && f.value !== "" && f.value !== "null");

                            // If no fields have data, skip this section entirely
                            if (activeFields.length === 0) return null;

                            return (
                                <DetailSection key={section.id} title={section.title}>
                                    {activeFields.map((field, idx) => (
                                        <DataRow key={idx} label={field.label} value={field.value} />
                                    ))}
                                    {section.id === "gps_location" && hasGPS && (
                                        <div className="col-span-2 mt-2">
                                            <div className="rounded-xl overflow-hidden border border-surface-border mb-2" style={{ height: 160 }}>
                                                <iframe title={`map-${index}`} width="100%" height="100%" frameBorder="0" src={`https://maps.google.com/maps?q=${capture.gpsLat},${capture.gpsLon}&z=16&output=embed`} allowFullScreen />
                                            </div>
                                            <div className="flex gap-2 mt-2">
                                                <a href={`https://www.google.com/maps?q=${capture.gpsLat},${capture.gpsLon}`} target="_blank" rel="noreferrer"
                                                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-surface border border-surface-border rounded-xl font-body text-xs text-text-primary hover:bg-primary/10 hover:border-primary/40 transition-colors">
                                                    <Globe className="w-3.5 h-3.5 text-primary" /> Google Maps
                                                </a>
                                                <a href={`https://maps.apple.com/?q=${capture.gpsLat},${capture.gpsLon}`} target="_blank" rel="noreferrer"
                                                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-surface border border-surface-border rounded-xl font-body text-xs text-text-primary hover:bg-primary/10 hover:border-primary/40 transition-colors">
                                                    🗺️ Apple Maps
                                                </a>
                                                <a href={`https://waze.com/ul?ll=${capture.gpsLat},${capture.gpsLon}&navigate=yes`} target="_blank" rel="noreferrer"
                                                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-surface border border-surface-border rounded-xl font-body text-xs text-text-primary hover:bg-primary/10 hover:border-primary/40 transition-colors">
                                                    🚗 Waze
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </DetailSection>
                            );
                        })}

                    </div>
                </div>
            )}
        </div>
    );
}

function DetailSection({ title, children }) {
    const validChildren = React.Children.toArray(children).filter(child => child !== null);

    if (validChildren.length === 0) return null;

    return (
        <div className="mb-4">
            <div className="font-body text-xs text-primary uppercase tracking-wider mb-2 pb-1 border-b border-surface-border">{title}</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">{validChildren}</div>
        </div>
    );
}

function DataRow({ label, value }) {
    if (value == null || value === "" || value === "null") return null;
    return (
        <div>
            <div className="font-body text-xs text-text-muted uppercase tracking-wider mb-0.5">{label}</div>
            <div className="font-mono text-xs text-text-primary break-all">{String(value)}</div>
        </div>
    );
}

function InfoRow({ label, children }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-6 py-4">
            <div className="font-body text-xs text-text-muted uppercase tracking-wider w-36 flex-shrink-0">{label}</div>
            <div className="flex items-center gap-2 flex-1 min-w-0">{children}</div>
        </div>
    );
}